import { yupResolver } from '@hookform/resolvers/yup';
import { Close } from '@mui/icons-material';
import { Box, Card, CardActions, CardContent, CardHeader, IconButton, Modal, useMediaQuery } from '@mui/material';
import { Dispatch, SetStateAction, useCallback, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { TalentProfileCreationModalFooter } from '../../../../../Hooks/TalentProfileCreation/TalentProfileCreationModalFooter';
import { useTalent } from '../../../../../Hooks/useTalent';
import { useToast } from '../../../../../Hooks/useToast';
import { useParseResumeMutation, useUpdateTalentMutation, useUpdateTalentResumeMutation, useUploadTalentProfilePictureMutation } from '../../../../../Redux/api/talentApi';
import { useAppDispatch, useAppSelector } from '../../../../../Redux/store';
import { TalentProfileCreationSteps } from '../../../../../Types/General';
import { GetEnumLength } from '../../../../../Utils/Object';
import { CertificationsArraySchema, CertificationsSchema, EducationSchema, ExperienceSchema, ExpertiseSchema, PersonalInfoSchema, PortfolioSchema } from '../../../../../Schemas/ResumeSchemas';
import { IsNullOrEmpty } from '../../../../../Utils/String';
import { processParsedResume, setProfilePicture, setTalentProfileCreationStep } from '../../Slices/TalentProfileCreationSlice';
import { CreatingProfile } from '../CreatingProfile';
import { PhotoUpload } from '../PhotoUpload';
import EducationForm from './Education/Education';
import { Expertise } from './Expertise';
import { ParsingResume } from './ParsingResume';
import { PersonalInformation } from './PersonalInformation';
import { Portfolio } from './Portfolio/Portfolio';
import { ResumeUpload } from './ResumeUpload';
import { WorkExperience } from './WorkExperience/WorkExperience';
import { WorkPreference } from './WorkPreference';
import { Talent } from '../../../../../Types/Talent';
import { Certifications } from './Certifications/Certifications';
import { useMobileView } from '../../../../../Hooks/useMobileView';

interface TalentCreationModalProps {
    open: boolean;
    setOpen: Dispatch<SetStateAction<boolean>>;
    hasResume: boolean;
}

const getDefaultPersonalValues = (talent: Talent | undefined) => {
    return {
        primaryLanguage: talent?.resume?.languages[0]?.name ?? '',
        secondaryLanguage: talent?.resume?.languages[1]?.name ?? '',
        summary: talent?.resume?.summary ?? '',
    }
}
const getDefaultExpertiseValues = (talent: Talent | undefined) => {
    return {
        specialization: talent?.resume?.expertise?.specialization ?? '',
        primaryExpertise: talent?.resume?.expertise?.primary_expertise ?? '',
        secondaryExpertise: talent?.resume?.expertise?.secondary_expertise ?? '',
        tertiaryExpertise: talent?.resume?.expertise?.tertiary_expertise ?? '',
        expertiseLevel: talent?.resume?.expertise?.expertise_level ?? '',
    }
}
const getDefaultEducationValues = (talent: Talent | undefined) => {
    return {
        title: talent?.resume?.education[0]?.title ?? '',
        major: talent?.resume?.education[0]?.major ?? '',
        school: talent?.resume?.education[0]?.school ?? '',
        start_date: talent?.resume?.education[0]?.start_date ?? '',
        end_date: talent?.resume?.education[0]?.end_date ?? '',
    }
}
const getDefaultExperienceValues = (talent: Talent | undefined) => {
    return {
        title: talent?.resume?.experience[0]?.title ?? '',
        company: talent?.resume?.experience[0]?.company ?? '',
        start_date: talent?.resume?.experience[0]?.start_date ?? '',
        end_date : talent?.resume?.experience[0]?.end_date ?? '',
        description: talent?.resume?.experience[0]?.description ?? '',
        present: talent?.resume?.experience[0]?.present ?? false
    }
}
const getDefaultPortfolioValues = (talent: Talent | undefined) => {
    return {
        urls: talent?.resume?.urls ?? []
    }
}
const getDefaultCertificationsValues = (talent: Talent | undefined) => {
    return {
        Certifications: talent?.resume?.certifications ?? []
    }
}


export const TalentCreationModal = (props: TalentCreationModalProps) => {
    /* ******************** Hooks ******************** */
    const { talent } = useTalent();
    const certifications = useAppSelector( state => state.TalentProfileCreation.certifications);
    const portfolio = useAppSelector( state => state.TalentProfileCreation.portfolio);
    const industry = useAppSelector( state => state.TalentProfileCreation.industry) ?? talent?.work_preferences?.industry;
    const site = useAppSelector( state => state.TalentProfileCreation.workSite)?? talent?.work_preferences?.site ;
    const { showToast } = useToast();
    const navigate = useNavigate();
    const [
        parseResume, 
        {
            data: response, 
            isLoading: responseLoading
        }
    ] = useParseResumeMutation();
    const [
        uploadPhoto, 
        {
            data: uploadResponse, 
            isLoading: isUploading
        }
    ] = useUploadTalentProfilePictureMutation(); 
    const [
        updateTalent, 
        {
            data: talentUpdateResponse,
             isLoading: talentUpdateResponseLoading
            }
    ] = useUpdateTalentMutation();
    
    const [
        updateResume,
        {
            data: resumeUpdateResponse,
            isLoading: resumeUpdateLoading
        }
    ] = useUpdateTalentResumeMutation();

    const dispatch = useAppDispatch();
    const { locator } = useTalent();
    const userId = useAppSelector( state => state.TalentProfileCreation.userId);
    const step = useAppSelector( state => state.TalentProfileCreation.step);

    //Forms
    const  personalInfoForm = useForm({
        defaultValues: getDefaultPersonalValues(talent),
        resolver: yupResolver(PersonalInfoSchema),
        mode: "onChange",
        reValidateMode: "onChange" 
    });
    const expertiseForm = useForm({
        defaultValues: getDefaultExpertiseValues(talent),
        resolver: yupResolver(ExpertiseSchema),
        mode: "onChange",
        reValidateMode: "onChange" 
    });
    const educationForm = useForm({
        defaultValues: getDefaultEducationValues(talent),
        resolver: yupResolver(EducationSchema),
        mode: "onChange",
        reValidateMode: "onChange" 
    });
    const experienceForm = useForm({
        defaultValues: getDefaultExperienceValues(talent),
        resolver: yupResolver(ExperienceSchema),
        mode: "onChange",
        reValidateMode: "onChange" 
    });
    const portfolioForm = useForm({
        defaultValues: getDefaultPortfolioValues(talent),
        resolver: yupResolver(PortfolioSchema),
        mode: "onChange",
        reValidateMode: "onChange" 
    });
    const certificationsForm = useForm({
        defaultValues: getDefaultCertificationsValues(talent),
        resolver: yupResolver(CertificationsArraySchema),
        mode: "onChange",
        reValidateMode: "onChange" 
    });
    /* ******************** Variables ******************** */
    const uploadedFile = useAppSelector( state => state.TalentProfileCreation.profilePicture);
    const [uploadedResume, setUploadedResume] = useState<File | undefined>(undefined);
    const { isMobileView } = useMobileView();
    /* ******************** Functions ******************** */
    const setUploadedFile= (file: File | undefined) => {
        dispatch(setProfilePicture(file));
    }
    const handleSave = (all: boolean = false) => {
        const isDirty = personalInfoForm.formState.isDirty ||
        expertiseForm.formState.isDirty ||
        educationForm.formState.isDirty ||
        experienceForm.formState.isDirty ||
        portfolioForm.formState.isDirty ||
        industry != talent?.work_preferences?.industry ||
        site != talent?.work_preferences?.site 

        const updated_resume: {[key: string]: any} = {
            keywords: talent?.resume?.keywords
        };
        // Add personal info
        if (personalInfoForm.formState.isValid) {
            const personalInfo = personalInfoForm.getValues();
            updated_resume.summary = personalInfo.summary

            //format languages;
            let languages = [{name: personalInfo.primaryLanguage, proficiency: ' '}];
            if (personalInfo.secondaryLanguage) languages.push({name: personalInfo.secondaryLanguage, proficiency: ' '});
            updated_resume.languages = languages;
        }
        //Add expertise
        if (expertiseForm.formState.isValid) {
            const expertise = expertiseForm.getValues();
            const expertiseObject: {[key: string]: any}  = {
                specialization: " ",
                primary_expertise: expertise.primaryExpertise,
                expertise_level: expertise.expertiseLevel
            }

            if (!IsNullOrEmpty(expertise.secondaryExpertise)) expertiseObject.secondary_expertise = expertise.secondaryExpertise;
            if (!IsNullOrEmpty(expertise.tertiaryExpertise)) expertiseObject.tertiary_expertise = expertise.tertiaryExpertise;
            updated_resume.expertise = expertiseObject;
        }
        //Add education
        if (educationForm.formState.isValid) {
            updated_resume.education = [
                {
                    ...educationForm.getValues()
                }
            ]
        }
        //Add experience
        if (experienceForm.formState.isValid) {
            updated_resume.experience = [
                {
                    ...experienceForm.getValues()
                }
            ]
        }
        
        //add certifications
        if (certificationsForm.formState.isValid) {
            updated_resume.certifications = certificationsForm.getValues().Certifications
        }
        //add portfolio
            if (portfolioForm.formState.isValid) {
                updated_resume.urls = portfolioForm.getValues("urls");
            }
        //add preferences
        const updated_preferences: {[key: string]: any} = {}
         updated_preferences.industry = " "
        if (site) updated_preferences.site = site

        if (isDirty) {
            if (all || step >= TalentProfileCreationSteps.Preferences) {
                const updateTalentRequestBody = {
                    locator: locator,
                    updated_data: {
                        resume: updated_resume,
                        title: updated_resume.expertise.primary_expertise,
                        work_preferences: updated_preferences
                    }
                }
                console.log(updateTalentRequestBody)
                updateTalent(updateTalentRequestBody)
                .unwrap()
                .then(() => navigate(`/profile/talent`))
                .catch(err => {
                    console.log(err)
                    showToast({
                        text: "Something went wrong",
                        variant: 'error'
                    })
                })
            } else {
                const updateTalentResumeBody = {
                    locator: locator!,
                    updated_data: updated_resume
                }
                console.log(updateTalentResumeBody)
                updateResume(updateTalentResumeBody)
                .unwrap()
                .catch(err => {
                    console.log(err)
                    showToast({
                        text: "Something went wrong",
                        variant: 'error'
                    })
                })
    
            }
        }
    }
    const handleNext = () =>{
        const nextStep = step+1
        if (step === TalentProfileCreationSteps.Parsing && uploadedResume) {
            let resumeFormData = new FormData();
            resumeFormData.append('file', uploadedResume);
            parseResume({locator: locator!, file: resumeFormData})
            .unwrap()
            .then( res => {
                dispatch(processParsedResume(res));
                dispatch(setTalentProfileCreationStep(nextStep));
            })
            .catch( err => {
                console.error('Error occured while parsing resume: ', err);
                showToast({
                    text: 'Unable to parse resume',
                    variant: 'error'
                });
            })
        } else if (nextStep <= GetEnumLength(TalentProfileCreationSteps)-1) {
            dispatch(setTalentProfileCreationStep(nextStep))
            return
        }
        
        if (step == TalentProfileCreationSteps.Photo)  {
            if (uploadedFile) {
                let photoFormData = new FormData();
                photoFormData.append('file', uploadedFile);
                uploadPhoto({locator: locator!, body: photoFormData})
                .unwrap()
                .then(res => console.log(res))
                .catch(err => console.log(err));
            }
            handleSave(true);
        }
    }

    const handleClose = () =>{ 
        handleSave();
        props.setOpen(false);
    }

    const getView = useCallback(() => {
        if (isUploading || talentUpdateResponseLoading) return <CreatingProfile /> 

        switch(step) {
            case TalentProfileCreationSteps.Parsing:
                return responseLoading ? <ParsingResume />: <ResumeUpload uploadedResume={uploadedResume} setUploadedResume={setUploadedResume}/>;
            case TalentProfileCreationSteps.Personal:
                return <PersonalInformation personalInfoForm={personalInfoForm}/>;
            case TalentProfileCreationSteps.Expertise:
                return <Expertise expertiseForm={expertiseForm} />;            
            case TalentProfileCreationSteps.Education:
                return <EducationForm educationForm={educationForm}/>;     
            case TalentProfileCreationSteps.Experience:
                return <WorkExperience experienceForm={experienceForm}/>;     
            case TalentProfileCreationSteps.Certifications:
                return <Certifications certificationsForm={certificationsForm} />;     
            case TalentProfileCreationSteps.Portfolio:
                return <Portfolio portfolioForm={portfolioForm}/>;  
            case TalentProfileCreationSteps.Preferences:
                return <WorkPreference />;  
            case TalentProfileCreationSteps.Photo:
                return <PhotoUpload uploadedFile={uploadedFile} setUploadedFile={setUploadedFile}/>;
        }
    }, [step, isUploading, talentUpdateResponseLoading]);
    const handleBack = () =>{
        const prevStep = step-1
        if (prevStep >= 1) {
            dispatch(setTalentProfileCreationStep(prevStep))
        } 
    }
    /* ******************** Effects ******************** */
    useEffect(()=> {
        if (talent) {
            personalInfoForm.reset(getDefaultPersonalValues(talent));
            expertiseForm.reset(getDefaultExpertiseValues(talent));
            educationForm.reset(getDefaultEducationValues(talent));
            experienceForm.reset(getDefaultExperienceValues(talent));
            portfolioForm.reset(getDefaultPortfolioValues(talent));
        }
    }, [talent])
    useEffect(() => {
        dispatch(setTalentProfileCreationStep(TalentProfileCreationSteps.Personal))
    }, [])
    /* ******************** JSX ******************** */
    return (
        <Modal
            open={props.open}
            sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center"
            }}
        >
            <Card
                sx={{
                    width: isMobileView ? "350px" : "600px",
                    borderRadius: "12px",
                    maxHeight: "95%",
                    overflowY: "auto"
                }}
            >
                <CardHeader 
                    sx={{
                        paddingBottom: 1
                    }}                
                    action={
                        <IconButton onClick={handleClose}>
                            <Close color='primary'/>
                        </IconButton>
                    }
                />
                <CardContent
                    sx={{
                        padding: '0 24px'
                    }}
                >
                    {
                        getView()
                    }
                </CardContent>
                <CardActions
                    sx={{
                        padding: '24px'
                    }}
                >
                    <Box display={'flex'} flexDirection={'row'} width={'100%'}>
                        {
                            isUploading || talentUpdateResponseLoading || responseLoading ?
                            null :
                            <TalentProfileCreationModalFooter 
                                handleBack={handleBack}
                                handleNext={handleNext}
                            />
                        }
                    </Box>
                </CardActions>
            </Card> 
        </Modal>
    )
}